/**
 * Configuration des étapes du tutoriel interactif
 */

import type { WarrantyFormState } from '../../hooks/useWarrantyFormWizard';
import { getWarrantyTypeWording } from '@/utils/vendorWording';
import type { VendorWording } from '@/utils/vendorWording';

export interface TutorialStep {
  id: string;
  fieldRef: string | string[];
  title: string;
  content: string;
  validation: (formData: WarrantyFormState) => boolean;
  wizardStep: number;
  fieldType: 'text' | 'select' | 'radio' | 'number';
  placement?: 'auto' | 'top';
}

/**
 * Génère les étapes du tutoriel avec le wording adapté au type (garantie/prestation)
 */
function buildTutorialSteps(wording: VendorWording): TutorialStep[] {
  return [
    // Step 1 - Informations
    {
      id: 'society',
      fieldRef: 'society-select',
      title: 'Sélectionnez une société',
      content: `Choisissez la société pour laquelle vous souhaitez créer cette ${wording.singularLower}. Cette société déterminera les options disponibles.`,
      validation: (formData) => formData.societyId !== undefined && (typeof formData.societyId === 'string' ? formData.societyId.length > 0 : Number(formData.societyId) > 0),
      wizardStep: 1,
      fieldType: 'select',
    },
    {
      id: 'warranty-type',
      fieldRef: 'warranty-type-select',
      title: 'Type de garantie/prestation',
      content: 'Choisissez si vous souhaitez créer une Garantie (couverture mécanique) ou une Prestation (service). Ce choix dépend du profil de votre société.',
      validation: (formData) => formData.warrantyType !== undefined,
      wizardStep: 1,
      fieldType: 'select',
    },
    {
      id: 'warranty-name',
      fieldRef: 'warranty-name-input',
      title: `Nommez votre ${wording.singularLower}`,
      content: `Donnez un nom descriptif à votre ${wording.singularLower}. Ce nom sera visible par vos clients.`,
      validation: (formData) => !!formData.name && formData.name.length >= 3,
      wizardStep: 1,
      fieldType: 'text',
    },
    {
      id: 'vehicle-type',
      fieldRef: 'vehicle-type-select',
      title: 'Type de véhicule',
      content: `Sélectionnez le type de véhicule couvert par cette ${wording.singularLower} (VP, VUL, Moto, etc.).`,
      validation: (formData) => formData.vehicleType !== undefined,
      wizardStep: 1,
      fieldType: 'select',
    },

    // Step 2 - Configuration
    {
      id: 'duration',
      fieldRef: 'duration-select',
      title: `Durée ${wording.deType}`,
      content: 'Choisissez la durée de couverture en mois. Les durées disponibles dépendent de votre société.',
      validation: (formData) => !!formData.duration && formData.duration > 0,
      wizardStep: 2,
      fieldType: 'select',
    },
    {
      id: 'financial-cap',
      fieldRef: 'financial-cap-input',
      title: 'Plafond financier',
      content: `Définissez le montant maximum de remboursement pour cette ${wording.singularLower} (en euros).`,
      validation: (formData) => !!formData.financialCap && formData.financialCap > 0,
      wizardStep: 2,
      fieldType: 'number',
    },
    {
      id: 'km-limit',
      fieldRef: 'km-limit-input',
      title: 'Limite kilométrique',
      content: `Indiquez le kilométrage maximum du véhicule pour bénéficier de cette ${wording.singularLower}.`,
      validation: (formData) => !!formData.kmLimit && formData.kmLimit > 0,
      wizardStep: 2,
      fieldType: 'number',
    },
    {
      id: 'provision',
      fieldRef: 'provision-input',
      title: 'Provision',
      content: 'Montant mis de côté pour les SAV potentiels. Ce montant est obligatoire si votre société le requiert.',
      validation: (formData) => !!formData.provision && formData.provision > 0,
      wizardStep: 2,
      fieldType: 'number',
    },
    {
      id: 'transfer-pricing',
      fieldRef: 'transfer-pricing-group',
      title: 'Transfer Pricing',
      content: 'Définissez le prix de rétrocession (montant reversé à la société mère) OU le prix d\'achat (montant payé par le membre). Les deux champs sont mutuellement exclusifs : si l\'un est rempli, l\'autre sera automatiquement à 0.',
      validation: (formData) => (formData.headTransferPrice !== undefined && formData.headTransferPrice >= 0) || (formData.memberTransferPrice !== undefined && formData.memberTransferPrice > 0),
      wizardStep: 2,
      fieldType: 'number',
    },
    {
      id: 'commission',
      fieldRef: 'commission-input',
      title: 'Commission vendeur',
      content: 'Définissez la commission du vendeur, soit en montant fixe (€), soit en pourcentage du prix de vente HT.',
      validation: (formData) => !!formData.commission && formData.commission > 0,
      wizardStep: 2,
      fieldType: 'number',
    },

    // Step 3 - Pièces
    {
      id: 'parts-selection',
      fieldRef: 'parts-recommendations-container',
      title: 'Recommandations de couverture',
      content: 'Appliquez une recommandation adaptée à votre offre en cliquant sur l\'une des cartes. Vous pourrez ensuite personnaliser la sélection si nécessaire.',
      validation: () => true,
      wizardStep: 3,
      fieldType: 'select',
    },
    {
      id: 'parts-toggle-list',
      fieldRef: 'parts-selection-container',
      title: 'Liste complète des pièces',
      content: 'Consultez la liste détaillée de toutes les pièces disponibles et sélectionnez celles que vous souhaitez couvrir. Vous pouvez naviguer par catégorie et cocher les pièces individuellement.',
      validation: (formData) => formData.selectedPartIds.length > 0,
      wizardStep: 3,
      fieldType: 'select',
    },

    // Step 4 - Tarification
    {
      id: 'payment-options',
      fieldRef: 'payment-options-group',
      title: 'Options de paiement',
      content: `Choisissez comment vos clients pourront payer cette ${wording.singularLower} : comptant uniquement, mensualisation, ou les deux.`,
      validation: (formData) => formData.isCashPayment === true || formData.isMensualisable === true,
      wizardStep: 4,
      fieldType: 'radio',
    },
    {
      id: 'selling-price',
      fieldRef: 'selling-price-input',
      title: 'Prix de vente',
      content: `Indiquez le prix de vente TTC ${wording.deType}. Si mensualisation uniquement, indiquez le prix mensuel.`,
      validation: (formData) => !!formData.sellingPrice && formData.sellingPrice > 0,
      wizardStep: 4,
      fieldType: 'number',
    },
  ];
}

/**
 * Obtient les étapes du tutoriel filtrées selon la disponibilité de la mensualisation,
 * le choix du type de garantie, le type d'utilisateur, et les paramètres de la société
 */
export function getFilteredTutorialSteps(
  _canMensualize: boolean,
  canChooseWarrantyType = true,
  isExternalUser = false,
  societyParams?: {
    /** Option rétrocession de la configuration effective de la société */
    isTransferPrice?: boolean;
    defineProvision?: boolean;
    commission?: boolean;
  },
  warrantyType?: number,
): TutorialStep[] {
  const wording = getWarrantyTypeWording(warrantyType);
  let steps = buildTutorialSteps(wording);

  if (isExternalUser) {
    steps = steps.filter(step => step.id !== 'society');
  }

  if (!canChooseWarrantyType) {
    steps = steps.filter(step => step.id !== 'warranty-type');
  }

  if (isExternalUser) {
    steps = steps.filter(step => step.id !== 'parts-selection');
  }

  if (!societyParams?.isTransferPrice) {
    steps = steps.filter(step => step.id !== 'transfer-pricing');
  }

  if (!societyParams || societyParams.commission !== true) {
    steps = steps.filter(step => step.id !== 'commission');
  }

  if (!societyParams || societyParams.defineProvision !== true) {
    steps = steps.filter(step => step.id !== 'provision');
  }

  return steps;
}
